import React, { useState } from "react";
import {
  FiActivity, FiMap, FiWind, FiCpu, FiAlertTriangle,
  FiFileText, FiSettings, FiBarChart2, FiShield, FiLayers
} from "react-icons/fi";
import { DashboardHeader } from "../../components/Shared";
import { aqmDummyData } from "../../Data/AqmData";

import AqmIndoorOverviewPanel  from "./panels/AqmIndoorOverviewPanel";
import AqmFloorMapPanel        from "./panels/AqmFloorMapPanel";
import AqmHvacPanel            from "./panels/AqmHvacPanel";
import AqmIndoorTrendsPanel    from "./panels/AqmIndoorTrendsPanel";
import AqmIndoorAiPanel        from "./panels/AqmIndoorAiPanel";
import AqmAlertsPanel          from "./panels/AqmAlertsPanel";
import AqmReportsPanel         from "./panels/AqmReportsPanel";

import AqmOutdoorOverviewPanel from "./panels/AqmOutdoorOverviewPanel";
import AqmStationMapPanel      from "./panels/AqmStationMapPanel";
import AqmPollutionPanel       from "./panels/AqmPollutionPanel";
import AqmCompliancePanel      from "./panels/AqmCompliancePanel";
import AqmOutdoorAiPanel       from "./panels/AqmOutdoorAiPanel";

import "./AqmDashboard.css";

const INDOOR_TABS = [
  { id: "overview",  label: "Overview",      icon: FiActivity   },
  { id: "floormap",  label: "Floor Map",     icon: FiMap        },
  { id: "hvac",      label: "HVAC Control",  icon: FiSettings   },
  { id: "trends",    label: "Trends",        icon: FiBarChart2  },
  { id: "ai",        label: "AI Insights",   icon: FiCpu        },
  { id: "alerts",    label: "Alerts",        icon: FiAlertTriangle },
  { id: "reports",   label: "Reports",       icon: FiFileText   },
];

const OUTDOOR_TABS = [
  { id: "overview",    label: "Overview",         icon: FiActivity    },
  { id: "stationmap",  label: "Station Map",      icon: FiMap         },
  { id: "pollution",   label: "Pollution",        icon: FiWind        },
  { id: "compliance",  label: "CPCB Compliance",  icon: FiShield      },
  { id: "ai",          label: "AI & Forecast",    icon: FiCpu         },
  { id: "alerts",      label: "Alerts",           icon: FiAlertTriangle },
  { id: "reports",     label: "Reports",          icon: FiFileText    },
];

const AqmDashboard = () => {
  const [mode, setMode]           = useState("indoor");
  const [indoorTab, setIndoorTab] = useState("overview");
  const [outdoorTab, setOutdoorTab] = useState("overview");
  const d = aqmDummyData;

  const indoorAlerts  = d.indoor.alerts.filter(a => a.severity === "Critical" && !a.ack).length;
  const outdoorAlerts = d.outdoor.alerts.filter(a => a.severity === "Critical" && !a.ack).length;

  const tabs  = mode === "indoor" ? INDOOR_TABS  : OUTDOOR_TABS;
  const active= mode === "indoor" ? indoorTab    : outdoorTab;
  const setTab= mode === "indoor" ? setIndoorTab : setOutdoorTab;
  const critCount = mode === "indoor" ? indoorAlerts : outdoorAlerts;

  return (
    <div className="fm-container">
      <DashboardHeader currentSolutionType="aqm" />

      {/* Mode Switcher */}
      <div className="aqm-mode-bar">
        <button
          className={`aqm-mode-btn-lg ${mode === "indoor" ? "aqm-mode-lg-active" : ""}`}
          onClick={() => setMode("indoor")}
        >
          🏢 Indoor AQM
          <span style={{ fontSize: 11, opacity: 0.8, display: "block" }}>Offices · Schools · Hospitals</span>
        </button>
        <button
          className={`aqm-mode-btn-lg ${mode === "outdoor" ? "aqm-mode-lg-active" : ""}`}
          onClick={() => setMode("outdoor")}
        >
          🌍 Outdoor / Ambient AQM
          <span style={{ fontSize: 11, opacity: 0.8, display: "block" }}>Smart City · Industrial · Campus</span>
        </button>
      </div>

      {/* Tab Bar */}
      <div className="fm-tab-bar">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`fm-tab ${active === tab.id ? "fm-tab-active" : ""}`}
            onClick={() => setTab(tab.id)}
          >
            <tab.icon size={15} />
            {tab.label}
            {tab.id === "alerts" && critCount > 0 && (
              <span className="fm-tab-badge">{critCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Panel */}
      <div className="fm-panel">
        {mode === "indoor" && (
          <>
            {indoorTab === "overview"  && <AqmIndoorOverviewPanel d={d.indoor} />}
            {indoorTab === "floormap"  && <AqmFloorMapPanel       d={d.indoor} />}
            {indoorTab === "hvac"      && <AqmHvacPanel           d={d.indoor} />}
            {indoorTab === "trends"    && <AqmIndoorTrendsPanel   d={d.indoor} />}
            {indoorTab === "ai"        && <AqmIndoorAiPanel       d={d.indoor} />}
            {indoorTab === "alerts"    && <AqmAlertsPanel alerts={d.indoor.alerts} mode="indoor" />}
            {indoorTab === "reports"   && <AqmReportsPanel        d={d.indoor} />}
          </>
        )}
        {mode === "outdoor" && (
          <>
            {outdoorTab === "overview"    && <AqmOutdoorOverviewPanel d={d.outdoor} />}
            {outdoorTab === "stationmap"  && <AqmStationMapPanel      d={d.outdoor} />}
            {outdoorTab === "pollution"   && <AqmPollutionPanel       d={d.outdoor} />}
            {outdoorTab === "compliance"  && <AqmCompliancePanel      d={d.outdoor} />}
            {outdoorTab === "ai"          && <AqmOutdoorAiPanel       d={d.outdoor} />}
            {outdoorTab === "alerts"      && <AqmAlertsPanel alerts={d.outdoor.alerts} mode="outdoor" />}
            {outdoorTab === "reports"     && <AqmReportsPanel         d={d.outdoor} />}
          </>
        )}
      </div>
    </div>
  );
};

export default AqmDashboard;
